import React from "react"
import { Link as GatsbyLink } from "gatsby"
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react"
import Layout from "../components/Layout"
import data from "../data/items"

const formatDate = date =>
  new Intl.DateTimeFormat("zh-TW", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date))

const formatDuration = duration => {
  const seconds = Number(duration)
  return Number.isFinite(seconds) ? `${Math.round(seconds / 60)} 分鐘` : null
}

const getEpisodeName = title => title.replace(/^Ep\s*\d+\s*:\s*/i, "")

const getSummary = description =>
  description
    .split(/(?:\n\s*--|\n?Timecode|p\.s\.)/i)[0]
    .replace(/\s+/g, " ")
    .trim()

export default function Home() {
  const idList = data.result.items
  const items = data.entities.items
  const latestEpisode = items[idList[0]]

  return (
    <Layout>
      <Box
        bg="brand.900"
        color="white"
        borderRadius={{ base: "14px", md: "16px" }}
        overflow="hidden"
        boxShadow={{
          base: "0 14px 32px rgba(12, 41, 39, 0.18)",
          md: "0 22px 56px rgba(12, 41, 39, 0.22)",
        }}
      >
        <Flex direction={{ base: "column", md: "row" }}>
          <Stack
            flex="1"
            spacing={{ base: 5, md: 6 }}
            justify="center"
            px={{ base: 5, md: 10, lg: 14 }}
            py={{ base: 7, md: 14 }}
          >
            <Heading
              as="h1"
              fontSize={{ base: "2xl", sm: "4xl", lg: "5xl" }}
              lineHeight={{ base: "1.18", lg: "1.1" }}
              letterSpacing="-0.035em"
              maxW={{ base: "none", md: "12ch" }}
            >
              頂樓加蓋 Overbuild
            </Heading>
            <Text
              color="whiteAlpha.800"
              fontSize={{ base: "md", md: "lg" }}
              lineHeight={{ base: "1.7", md: "1.8" }}
              maxW="34rem"
            >
              一個講科技講產品的
              podcast，不一定有辦法讓你每次都學到東西，但至少讓你不無聊。
            </Text>

            <Box
              display={{ base: "block", md: "none" }}
              pt="4"
              borderTopWidth="1px"
              borderColor="whiteAlpha.300"
            >
              <Text color="brand.100" fontSize="xs" fontWeight="700" mb="1">
                最新一集
              </Text>
              <Text fontWeight="800" lineHeight="1.45">
                {getEpisodeName(latestEpisode.title)}
              </Text>
              <Text mt="1" color="whiteAlpha.700" fontSize="sm">
                {formatDate(latestEpisode.isoDate || latestEpisode.pubDate)}
                {latestEpisode.itunes && latestEpisode.itunes.duration
                  ? ` · ${formatDuration(latestEpisode.itunes.duration)}`
                  : ""}
              </Text>
            </Box>

            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing="3"
              align={{ base: "stretch", sm: "flex-start" }}
            >
              <Button
                as={GatsbyLink}
                to={`/ep/${latestEpisode.id}`}
                minH="48px"
                bg="white"
                color="brand.900"
                _hover={{ bg: "brand.50", transform: "translateY(-2px)" }}
                transition="all 180ms ease"
              >
                播放最新一集
              </Button>
              <Button
                as="a"
                href="https://overbuild.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                minH="48px"
                variant="outline"
                borderColor="whiteAlpha.500"
                color="white"
                _hover={{ bg: "whiteAlpha.200", borderColor: "whiteAlpha.700" }}
              >
                訂閱電子報
              </Button>
            </Stack>
          </Stack>

          <Box
            display={{ base: "none", md: "block" }}
            position="relative"
            w={{ base: "100%", md: "42%" }}
            minH={{ base: "300px", md: "470px" }}
          >
            <Image
              src={latestEpisode.itunes.image}
              alt={`${latestEpisode.title} 節目封面`}
              position="absolute"
              inset="0"
              w="100%"
              h="100%"
              objectFit="cover"
              loading="eager"
            />
            <Box
              position="absolute"
              inset="0"
              bg="linear-gradient(180deg, transparent 50%, rgba(12, 41, 39, 0.84) 100%)"
            />
            <Box position="absolute" left="6" right="6" bottom="6">
              <Badge bg="white" color="brand.900" mb="3">
                最新一集
              </Badge>
              <Heading
                as="h2"
                fontSize={{ base: "xl", md: "2xl" }}
                lineHeight="1.35"
              >
                {getEpisodeName(latestEpisode.title)}
              </Heading>
            </Box>
          </Box>
        </Flex>
      </Box>

      <Flex
        mt={{ base: 10, md: 20 }}
        mb="6"
        align={{ base: "flex-start", sm: "end" }}
        justify="space-between"
        direction={{ base: "column", sm: "row" }}
        sx={{ gap: "0.5rem" }}
      >
        <Heading
          as="h2"
          fontSize={{ base: "2xl", md: "3xl" }}
          letterSpacing="-0.025em"
        >
          所有集數
        </Heading>
        <Text color="blackAlpha.600" fontSize="sm" fontWeight="600">
          共 {idList.length} 集
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={{ md: 10 }}>
        {idList.map(id => {
          const episode = items[id]
          const duration = formatDuration(
            episode.itunes && episode.itunes.duration
          )

          return (
            <Link
              key={`to-ep${id}`}
              as={GatsbyLink}
              to={`/ep/${id}`}
              aria-label={`查看第 ${id} 集：${getEpisodeName(episode.title)}`}
              data-testid={`episode-link-${id}`}
              display="block"
              w="100%"
              minW="0"
              py={{ base: 5, md: 7 }}
              borderTopWidth="1px"
              borderColor="blackAlpha.200"
              _hover={{ textDecoration: "none" }}
              _active={{ bg: "brand.50" }}
              sx={{
                "&:hover .episode-title": { color: "brand.600" },
                "&:hover .episode-action": {
                  background: "var(--chakra-colors-brand-600)",
                  color: "white",
                  transform: "translateX(3px)",
                },
              }}
            >
              <Box display="flex" flexDirection="column" minW="0">
                <Flex justify="space-between" align="flex-start">
                  <Text
                    mb="3"
                    color="blackAlpha.600"
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.04em"
                  >
                    <Box as="span" color="brand.700">
                      EP {id}
                    </Box>{" "}
                    · {formatDate(episode.isoDate || episode.pubDate)}
                    {duration && ` · ${duration}`}
                  </Text>
                </Flex>
                <Heading
                  className="episode-title"
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  lineHeight="1.4"
                  letterSpacing="-0.015em"
                  transition="color 180ms ease"
                  order="2"
                >
                  {getEpisodeName(episode.title)}
                </Heading>
                <Text
                  mt="3"
                  color="blackAlpha.700"
                  fontSize={{ base: "md", md: "sm" }}
                  lineHeight="1.7"
                  order={{ base: 4, md: 3 }}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    "@media screen and (min-width: 48em)": {
                      WebkitLineClamp: 3,
                    },
                  }}
                >
                  {getSummary(episode.description)}
                </Text>
                <Flex
                  mt={{ base: 3, md: 4 }}
                  justify="flex-end"
                  order={{ base: 3, md: 4 }}
                >
                  <Flex
                    className="episode-action"
                    minH={{ base: "44px", md: "36px" }}
                    px={{ base: 4, md: 3 }}
                    align="center"
                    bg="brand.50"
                    color="brand.700"
                    borderRadius="10px"
                    fontSize="sm"
                    fontWeight="800"
                    transition="all 180ms ease"
                  >
                    查看本集
                    <Box
                      as="svg"
                      aria-hidden="true"
                      focusable="false"
                      viewBox="0 0 20 20"
                      w="20px"
                      h="20px"
                      ml="2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 10h11M11 6l4 4-4 4" />
                    </Box>
                  </Flex>
                </Flex>
              </Box>
            </Link>
          )
        })}
      </SimpleGrid>
    </Layout>
  )
}
